import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  InputAdornment,
  IconButton,
  Divider,
  Chip,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  Autocomplete,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress
} from '@mui/material';
import {
  Search as SearchIcon,
  MoreVert as MoreVertIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  Share as ShareIcon,
  Download as DownloadIcon,
  Label as LabelIcon,
  FilterList as FilterListIcon
} from '@mui/icons-material';

export interface Conversation {
  id: string;
  title?: string;
  tags: string[];
  isFavorite: boolean;
  createdAt: Date;
  updatedAt: Date;
  messageCount?: number;
  lastMessage?: string;
  relevanceScore?: number;
}

interface ConversationHistoryProps {
  conversations: Conversation[];
  selectedConversationId?: string;
  onSelectConversation: (conversationId: string) => void;
  onDeleteConversation: (conversationId: string, permanent?: boolean) => void;
  onRenameConversation: (conversationId: string, newTitle: string) => void;
  onSearchConversations: (query?: string, tags?: string[], favoritesOnly?: boolean) => void;
  onUpdateConversation: (conversationId: string, updates: { title?: string; tags?: string[]; isFavorite?: boolean }) => void;
  onExportConversation: (conversationId: string, format: 'json' | 'pdf' | 'txt' | 'csv') => void;
  onShareConversation: (conversationId: string, email: string, permission: 'read' | 'comment' | 'edit') => void;
  availableTags: string[];
  userPlan: 'trial' | 'pro' | 'team' | 'enterprise';
  isLoading?: boolean;
}

export const ConversationHistory: React.FC<ConversationHistoryProps> = ({
  conversations,
  selectedConversationId,
  onSelectConversation,
  onDeleteConversation,
  onRenameConversation: _onRenameConversation,
  onSearchConversations,
  onUpdateConversation,
  onExportConversation,
  onShareConversation,
  availableTags,
  userPlan,
  isLoading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTags, setSelectedTags] = useState<string[]>([]);
  const [favoritesOnly, setFavoritesOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  
  // Dialog states
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Form states
  const [editTitle, setEditTitle] = useState('');
  const [editTags, setEditTags] = useState<string[]>([]);
  const [shareEmail, setShareEmail] = useState('');
  const [sharePermission, setSharePermission] = useState<'read' | 'comment' | 'edit'>('read');
  const [exportFormat, setExportFormat] = useState<'json' | 'pdf' | 'txt' | 'csv'>('json');
  const [permanentDelete, setPermanentDelete] = useState(false);
  
  // Loading states
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Trigger search when filters change
    onSearchConversations(searchQuery || undefined, selectedTags, favoritesOnly);
  }, [searchQuery, selectedTags, favoritesOnly]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleTagsChange = (_event: unknown, newValue: string[]) => {
    setSelectedTags(newValue);
  };

  const handleFavoritesToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFavoritesOnly(event.target.checked);
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, conversation: Conversation) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
    setSelectedConversation(conversation);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedConversation(null);
  };

  const handleFavoriteToggle = async (conversation: Conversation) => {
    try {
      setActionLoading(true);
      await onUpdateConversation(conversation.id, { isFavorite: !conversation.isFavorite });
    } catch (err) {
      setError('Failed to update favorite status');
    } finally {
      setActionLoading(false);
    }
  };

  const openEditDialog = () => {
    if (selectedConversation) {
      setEditTitle(selectedConversation.title || '');
      setEditTags(selectedConversation.tags || []);
      setEditDialogOpen(true);
    }
    handleMenuClose();
  };

  const handleEditSave = async () => {
    if (!selectedConversation) return;
    
    try {
      setActionLoading(true);
      await onUpdateConversation(selectedConversation.id, {
        title: editTitle.trim() || undefined,
        tags: editTags
      });
      setEditDialogOpen(false);
    } catch (err) {
      setError('Failed to update conversation');
    } finally {
      setActionLoading(false);
    }
  };

  const openShareDialog = () => {
    if (userPlan === 'trial' || userPlan === 'pro') {
      setError('Sharing requires team or enterprise plan');
      handleMenuClose();
      return;
    }
    setShareEmail('');
    setSharePermission('read');
    setShareDialogOpen(true);
    handleMenuClose();
  };

  const handleShareSave = async () => {
    if (!selectedConversation || !shareEmail.trim()) return;
    
    try {
      setActionLoading(true);
      await onShareConversation(selectedConversation.id, shareEmail.trim(), sharePermission);
      setShareDialogOpen(false);
    } catch (err) {
      setError('Failed to share conversation');
    } finally {
      setActionLoading(false);
    }
  };

  const openExportDialog = () => {
    setExportFormat('json');
    setExportDialogOpen(true);
    handleMenuClose();
  };

  const handleExportSave = async () => {
    if (!selectedConversation) return;
    
    try {
      setActionLoading(true);
      await onExportConversation(selectedConversation.id, exportFormat);
      setExportDialogOpen(false);
    } catch (err) {
      setError('Failed to export conversation');
    } finally {
      setActionLoading(false);
    }
  };

  const openDeleteDialog = () => {
    setPermanentDelete(false);
    setDeleteDialogOpen(true);
    handleMenuClose();
  };

  const handleDeleteConfirm = async () => {
    if (!selectedConversation) return;
    
    try {
      setActionLoading(true);
      await onDeleteConversation(selectedConversation.id, permanentDelete);
      setDeleteDialogOpen(false);
    } catch (err) {
      setError('Failed to delete conversation');
    } finally {
      setActionLoading(false);
    }
  };

  const formatDate = (date: Date) => {
    const now = new Date();
    const diffInHours = (now.getTime() - date.getTime()) / (1000 * 60 * 60);
    
    if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 24 * 7) {
      return date.toLocaleDateString([], { weekday: 'short' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  const getConversationTitle = (conversation: Conversation) => {
    return conversation.title || 
           conversation.lastMessage?.substring(0, 50) + '...' || 
           'New Conversation';
  };

  return (
    <Paper sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} variant="outlined">
      {/* Header */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h6">
            Conversations
          </Typography>
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            color={showFilters ? 'primary' : 'default'}
          >
            <FilterListIcon fontSize="small" />
          </IconButton>
        </Box>
        
        {/* Search */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search conversations..."
          value={searchQuery}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
          sx={{ mb: showFilters ? 2 : 0 }}
        />

        {/* Filters */}
        {showFilters && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Autocomplete
              multiple
              size="small"
              options={availableTags}
              value={selectedTags}
              onChange={handleTagsChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Filter by tags..."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <LabelIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    variant="outlined"
                    label={option}
                    size="small"
                    {...getTagProps({ index })}
                    key={option}
                  />
                ))
              }
            />
            
            <FormControlLabel
              control={
                <Switch
                  checked={favoritesOnly}
                  onChange={handleFavoritesToggle}
                  size="small"
                />
              }
              label="Favorites only"
            />
          </Box>
        )}
      </Box>

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ m: 2, mb: 0 }}
        >
          {error}
        </Alert>
      )}

      {/* Conversation List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <CircularProgress size={24} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Loading conversations...
            </Typography>
          </Box>
        ) : conversations.length === 0 ? (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              {searchQuery || selectedTags.length > 0 || favoritesOnly ? 'No conversations found' : 'No conversations yet'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0 }}>
            {conversations.map((conversation, index) => (
              <React.Fragment key={conversation.id}>
                <ListItem sx={{ p: 0 }}>
                  <ListItemButton
                    selected={selectedConversationId === conversation.id}
                    onClick={() => onSelectConversation(conversation.id)}
                    sx={{ px: 2, py: 1.5 }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, mr: 1 }}>
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFavoriteToggle(conversation);
                              }}
                              disabled={actionLoading}
                              sx={{ mr: 0.5, p: 0.25 }}
                            >
                              {conversation.isFavorite ? (
                                <StarIcon fontSize="small" color="warning" />
                              ) : (
                                <StarBorderIcon fontSize="small" />
                              )}
                            </IconButton>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: selectedConversationId === conversation.id ? 600 : 400,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                flex: 1
                              }}
                            >
                              {getConversationTitle(conversation)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={(e) => handleMenuOpen(e, conversation)}
                            sx={{ opacity: 0.7, '&:hover': { opacity: 1 } }}
                          >
                            <MoreVertIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      }
                      secondary={
                        <Box sx={{ mt: 0.5 }}>
                          {/* Tags */}
                          {conversation.tags && conversation.tags.length > 0 && (
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 0.5 }}>
                              {conversation.tags.slice(0, 3).map((tag) => (
                                <Chip
                                  key={tag}
                                  label={tag}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              ))}
                              {conversation.tags.length > 3 && (
                                <Chip
                                  label={`+${conversation.tags.length - 3}`}
                                  size="small"
                                  variant="outlined"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              )}
                            </Box>
                          )}
                          
                          {/* Date and message count */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <Typography variant="caption" color="text.secondary">
                              {formatDate(conversation.updatedAt)}
                            </Typography>
                            {conversation.messageCount && (
                              <Chip
                                label={conversation.messageCount}
                                size="small"
                                variant="outlined"
                                sx={{ height: 16, fontSize: '0.7rem' }}
                              />
                            )}
                          </Box>
                        </Box>
                      }
                    />
                  </ListItemButton>
                </ListItem>
                {index < conversations.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <MenuItem onClick={openEditDialog}>
          <EditIcon fontSize="small" sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={openExportDialog}>
          <DownloadIcon fontSize="small" sx={{ mr: 1 }} />
          Export
        </MenuItem>
        {(userPlan === 'team' || userPlan === 'enterprise') && (
          <MenuItem onClick={openShareDialog}>
            <ShareIcon fontSize="small" sx={{ mr: 1 }} />
            Share
          </MenuItem>
        )}
        <Divider />
        <MenuItem onClick={openDeleteDialog} sx={{ color: 'error.main' }}>
          <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Conversation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Title"
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            margin="normal"
            placeholder="Enter conversation title..."
          />
          <Autocomplete
            multiple
            freeSolo
            options={availableTags}
            value={editTags}
            onChange={(_event, newValue) => setEditTags(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Tags"
                placeholder="Add tags..."
                margin="normal"
              />
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="outlined"
                  label={option}
                  {...getTagProps({ index })}
                  key={option}
                />
              ))
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleEditSave} 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Share Dialog */}
      <Dialog open={shareDialogOpen} onClose={() => setShareDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Share Conversation</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={shareEmail}
            onChange={(e) => setShareEmail(e.target.value)}
            margin="normal"
            placeholder="Enter email address..."
          />
          <FormControl fullWidth margin="normal">
            <InputLabel>Permission</InputLabel>
            <Select
              value={sharePermission}
              onChange={(e) => setSharePermission(e.target.value as 'read' | 'comment' | 'edit')}
              label="Permission"
            >
              <MenuItem value="read">Read only</MenuItem>
              <MenuItem value="comment">Can comment</MenuItem>
              <MenuItem value="edit">Can edit</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleShareSave} 
            variant="contained"
            disabled={actionLoading || !shareEmail.trim()}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Share'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={() => setExportDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Export Conversation</DialogTitle>
        <DialogContent>
          <FormControl fullWidth margin="normal">
            <InputLabel>Format</InputLabel>
            <Select
              value={exportFormat}
              onChange={(e) => setExportFormat(e.target.value as 'json' | 'pdf' | 'txt' | 'csv')}
              label="Format"
            >
              <MenuItem value="json">JSON</MenuItem>
              <MenuItem value="txt">Text</MenuItem>
              <MenuItem value="csv">CSV</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setExportDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleExportSave} 
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Conversation</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this conversation?
          </Typography>
          <FormControlLabel
            control={
              <Switch
                checked={permanentDelete}
                onChange={(e) => setPermanentDelete(e.target.checked)}
              />
            }
            label="Permanent deletion (cannot be recovered)"
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleDeleteConfirm} 
            variant="contained"
            color="error"
            disabled={actionLoading}
          >
            {actionLoading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};